import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError } from '@/auth/api';
import { PrimaryButton } from '@/components/PrimaryButton';
import { revertInternalSale } from '@/features/operations/api';
import { formatPrice } from '@/features/operations/money';
import type { DeviceDetail, DeviceSale } from '@/features/operations/types';

/** ADMIN-only correction of a recorded sale (VEN-8). */
export function RevertSaleDialog({
  open,
  token,
  deviceId,
  sale,
  onClose,
  onReverted,
}: {
  open: boolean;
  token: string;
  deviceId: string;
  sale: DeviceSale;
  onClose: (stale: boolean) => void;
  onReverted: (device: DeviceDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setReason('');
    setReasonError(null);
    setRequestError(null);
    setStale(false);
  };

  const close = () => {
    if (isSaving) return;
    const wasStale = stale;
    reset();
    onClose(wasStale);
  };

  const handleRevert = async () => {
    if (!reason.trim()) {
      setReasonError('Contá por qué se revierte la venta.');
      return;
    }
    setIsSaving(true);
    setRequestError(null);
    try {
      const updated = await revertInternalSale(token, deviceId, reason.trim());
      reset();
      onReverted(updated);
    } catch (cause) {
      if (cause instanceof ApiError && (cause.status === 404 || cause.status === 409)) {
        setStale(true);
      } else {
        setRequestError('No pudimos revertir la venta. Volvé a intentar.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Revertir venta</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {stale ? (
            <Alert severity="error">
              La venta o el equipo cambiaron recientemente. Cerrá este diálogo para actualizar la
              ficha antes de volver a intentar.
            </Alert>
          ) : null}
          {requestError ? <Alert severity="error">{requestError}</Alert> : null}
          <Typography variant="body2">
            La venta de <strong>{formatPrice(sale.finalPriceMinor)}</strong> quedará en el
            historial. El equipo volverá a <strong>Pendiente de publicación</strong> y se borrarán
            de su ficha la fecha de venta y la garantía congelada. No se publicará automáticamente.
          </Typography>
          {sale.paymentMethod === 'MERCADO_PAGO' && sale.reservationReference ? (
            <Alert severity="warning">
              Esta acción no devuelve el dinero. Después, devolvé el pago completo desde Mercado
              Pago y registrá la devolución en esta ficha.
            </Alert>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            required
            label="Motivo"
            value={reason}
            disabled={isSaving || stale}
            error={Boolean(reasonError)}
            helperText={
              reasonError ??
              'Queda en el historial de la venta y del estado, con tu usuario y fecha.'
            }
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={isSaving} onClick={close}>
          {stale ? 'Cerrar' : 'Volver'}
        </Button>
        {stale ? null : (
          <PrimaryButton disabled={isSaving} onClick={() => void handleRevert()}>
            {isSaving ? 'Revirtiendo...' : 'Revertir venta'}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
